/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { Button, Chip, LinkButton, Skeleton, StatusTag, TableCard } from '../../components/console/ui';
import { invoiceService, kycService, walletService } from '../../services/api';
import { useWallet } from '../../hooks/useWallet';
import type { InvoiceStatus, KYC } from '../../types';
import GenerateInvoiceModal from '../../components/billing/GenerateInvoiceModal';
import BillingDetailsCard, {
  EMPTY_BILLING_DETAILS,
  kycBillingDefaults,
  mergeBillingDefaults,
} from '../../components/billing/BillingDetailsCard';
import { downloadInvoicePDF, invoicePeriod, money } from '../../components/billing/billingUtils';
import {
  openRazorpayCheckout,
  useBillingContext,
  useBillingDetails,
  useInvoices,
} from '../../components/billing/useBilling';

type Filter = 'all' | InvoiceStatus;

const FILTERS: Array<{ value: Filter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'paid', label: 'Paid' },
  { value: 'pending', label: 'Pending' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'cancelled', label: 'Cancelled' },
];

const BillingInvoices: React.FC = () => {
  const qc = useQueryClient();
  const { currency, exchangeRate } = useBillingContext();
  const { wallet } = useWallet();
  const { invoices, isLoading } = useInvoices();
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [showGenerate, setShowGenerate] = useState(false);

  // Editable "Billed to" details (GET/PUT /billing-details). null = older
  // backend without the endpoint: fall back to the read-only KYC view.
  const { data: billing, isLoading: billingLoading } = useBillingDetails();

  // KYC values prefill billing details (and are the fallback view). The
  // endpoint 404s (and toasts) when KYC was never submitted, so only ask once
  // the wallet says it's verified.
  const { data: kyc, isLoading: kycLoading } = useQuery({
    queryKey: ['kyc'],
    queryFn: () => kycService.getStatus().then((res) => res.data as KYC),
    enabled: !!wallet?.kyc_verified,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const sum = (s: InvoiceStatus) => invoices.filter((i) => i.status === s).reduce((a, i) => a + (i.total_amount || 0), 0);
  const filtered = useMemo(
    () =>
      invoices.filter(
        (i) =>
          (filter === 'all' || i.status === filter) &&
          (!search || i.invoice_number.toLowerCase().includes(search.toLowerCase())),
      ),
    [invoices, filter, search],
  );

  const pay = useMutation({
    mutationFn: (invoiceId: string) => invoiceService.pay(invoiceId),
    onSuccess: async (response) => {
      await openRazorpayCheckout(response.data, async (r) => {
        try {
          await walletService.verifyPayment(r.razorpay_order_id, r.razorpay_payment_id, r.razorpay_signature);
          toast.success('Invoice paid successfully!');
          qc.invalidateQueries({ queryKey: ['invoices'] });
          qc.invalidateQueries({ queryKey: ['wallet'] });
        } catch {
          toast.error('Payment verification failed');
        }
      });
    },
    onError: () => toast.error('Failed to initiate payment'),
  });

  const legalName = kyc?.gst_verified_legal_name || kyc?.organisation_name || kyc?.business_name || kyc?.full_name;
  const address = kyc
    ? [kyc.address_line1, kyc.address_line2, kyc.city, kyc.state, kyc.pin_code].filter(Boolean).join(', ')
    : '';
  // What the printable invoice shows under "Billed to".
  const billTo = billing
    ? billing.source === 'saved'
      ? billing.billing_details
      : mergeBillingDefaults({ ...EMPTY_BILLING_DETAILS, ...billing.billing_details }, kycBillingDefaults(kyc))
    : kyc
      ? mergeBillingDefaults(EMPTY_BILLING_DETAILS, kycBillingDefaults(kyc))
      : null;

  return (
    <>
      <div className="grid gap-3.5 grid-cols-[repeat(auto-fit,minmax(min(100%,200px),1fr))]">
        {[
          { label: 'Total paid', v: sum('paid') },
          { label: 'Pending', v: sum('pending') },
          { label: 'Overdue', v: sum('overdue') },
        ].map((s) => (
          <div key={s.label} className="ps-card p-4">
            <div className="text-ps-subtle text-[13px]">{s.label}</div>
            {isLoading ? (
              <Skeleton className="h-7 w-28 mt-1" />
            ) : (
              <div className="text-2xl font-semibold mt-1 text-ps-text">{money(s.v, currency, exchangeRate)}</div>
            )}
          </div>
        ))}
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        {FILTERS.map((f) => (
          <Chip key={f.value} on={filter === f.value} onClick={() => setFilter(f.value)}>
            {f.label}
          </Chip>
        ))}
        <span className="flex-1" />
        {invoices.length > 0 && (
          <>
            <label className="sr-only" htmlFor="inv-search">
              Search invoices
            </label>
            <input
              id="inv-search"
              className="ps-input !w-auto min-w-[200px]"
              placeholder="Search invoice number"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </>
        )}
        <Button onClick={() => setShowGenerate(true)}>Generate invoice</Button>
      </div>

      <div className="flex flex-wrap gap-5 items-start">
        <div className="flex-[2_1_420px] min-w-0">
          {isLoading ? (
            <Skeleton className="h-48" />
          ) : filtered.length === 0 ? (
            <section className="ps-card p-8 flex flex-col gap-2.5 items-start justify-center" aria-labelledby="none">
              <h2 id="none" className="m-0 text-lg font-medium text-ps-text">
                {invoices.length === 0 ? 'No invoices yet' : 'No invoices match'}
              </h2>
              <p className="m-0 text-ps-muted text-sm leading-relaxed max-w-[460px]">
                {invoices.length === 0
                  ? `You’re on ${wallet?.account_type === 'postpaid' ? 'postpaid' : 'prepaid'} billing. Generated invoices appear here with their status and a PDF download.`
                  : 'Try a different status or search.'}
              </p>
            </section>
          ) : (
            <TableCard>
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Period</th>
                  <th className="!text-right">Amount</th>
                  <th>Status</th>
                  <th>Due</th>
                  <th className="!text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((inv) => (
                  <tr key={inv.id}>
                    <td>
                      <div className="font-mono text-ps-text">{inv.invoice_number}</div>
                      <div className="text-xs text-ps-subtle">{format(new Date(inv.created_at), 'd MMM yyyy')}</div>
                    </td>
                    <td className="text-ps-muted">{invoicePeriod(inv)}</td>
                    <td className="text-right">
                      <div className="text-ps-text">{money(inv.total_amount || 0, currency, exchangeRate)}</div>
                      {inv.tax_amount > 0 && (
                        <div className="text-xs text-ps-subtle">incl. {money(inv.tax_amount, currency, exchangeRate)} GST</div>
                      )}
                    </td>
                    <td>
                      <StatusTag status={inv.status} />
                    </td>
                    <td className="text-ps-muted">{format(new Date(inv.due_date), 'd MMM yyyy')}</td>
                    <td>
                      <div className="flex gap-2 justify-end">
                        <Button variant="secondary" size="sm" onClick={() => downloadInvoicePDF(inv, billTo)}>
                          PDF
                        </Button>
                        {(inv.status === 'pending' || inv.status === 'overdue') && (
                          <Button size="sm" onClick={() => pay.mutate(inv.id)} disabled={pay.isPending}>
                            {pay.isPending && pay.variables === inv.id ? 'Processing…' : 'Pay now'}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableCard>
          )}
        </div>

        {billingLoading || billing ? (
          <BillingDetailsCard
            details={{ ...EMPTY_BILLING_DETAILS, ...(billing?.billing_details || {}) }}
            source={billing?.source || 'empty'}
            kyc={kyc}
            loading={billingLoading || (!!wallet?.kyc_verified && kycLoading && billing?.source !== 'saved')}
          />
        ) : (
          <section className="ps-card p-5 flex flex-col gap-3 flex-[1_1_320px] min-w-0" aria-labelledby="gst">
            <h2 id="gst" className="m-0 text-base font-medium text-ps-text">
              Billing details on invoices
            </h2>
            {wallet?.kyc_verified && kycLoading ? (
              <Skeleton className="h-24" />
            ) : kyc ? (
              <>
                <dl className="m-0 flex flex-col gap-2.5 text-sm">
                  {[
                    ['Legal name', legalName],
                    ['GSTIN', kyc.gst_number],
                    ['Billing address', address],
                  ].map(([k, v]) => (
                    <div key={k} className="flex flex-col gap-0.5">
                      <dt className="text-ps-muted text-[13px]">{k}</dt>
                      <dd className="m-0 text-ps-text break-words">{v || '-'}</dd>
                    </div>
                  ))}
                </dl>
                <p className="m-0 text-xs text-ps-subtle">These come from your verified KYC. Contact support to change them.</p>
              </>
            ) : (
              <>
                <p className="m-0 text-sm text-ps-muted leading-relaxed">
                  Invoices use the legal name, GSTIN and address from your KYC. Complete KYC to add them.
                </p>
                <LinkButton to="/kyc" variant="secondary" className="self-start">
                  Complete KYC
                </LinkButton>
              </>
            )}
          </section>
        )}
      </div>

      <GenerateInvoiceModal open={showGenerate} onClose={() => setShowGenerate(false)} />
    </>
  );
};

export default BillingInvoices;
